import React, { type ReactNode, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DepartmentIcon from '@atlaskit/icon/core/department';
import LocationIcon from '@atlaskit/icon/core/location';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import ReportingLines from '@atlassian/reporting-lines';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { messages } from '../../messages';
import {
	onPersonCardOrgChartCardClicked,
	onPersonCardOrgChartDwelled,
	onPersonCardOrgChartShown,
} from '../analytics';
import { KnowledgeCardHeader } from '../common/knowledge-card-header';
import { KnowledgeCardsTopics } from '../knowledge-cards-topics';
import { KnowledgeCardsPicture } from '../styled';
import { KnowledgeCardsIntentType } from '../types';
import { useTeamsAppLinks } from '../use-teams-app-links';

const ORG_CHART_DWELL_THRESHOLD = 3000;

type KnowledgeCardsHeaderProps = {
	name: string;
	accountId?: string;
	email?: string | null;
	avatarUrl?: string | null;
	/**
	 * @deprecated pass accountId & allow the URL to be generated by the navigation library
	 */
	profileUrl?: string | null;
	jobTitle?: string | null;
	department?: string | null;
	location?: string | null;
	time?: string | null;
};

const IconPaddingStyles = cssMap({
	default: {
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

export const PersonCardHeader = ({
	name,
	accountId,
	email,
	avatarUrl,
	profileUrl,
	jobTitle,
	department,
	location,
	time,
}: KnowledgeCardsHeaderProps): React.JSX.Element => {
	const { commonAttributes } = useAIAnswerContext();
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { cloudId, isHello, sessionId } = commonAttributes;
	const showAITopics = fg('kd_topics_in_person_card');
	const { profileHref, profileOnNavigate } = useTeamsAppLinks({
		type: KnowledgeCardsIntentType.PERSON,
		currentUserId: accountId ?? '',
		cloudId,
	});

	const [isOrgChartModalOpen, setIsOrgChartModalOpen] = useState(false);

	useEffect(() => {
		let dwellTimeout: NodeJS.Timeout;

		if (isOrgChartModalOpen) {
			dwellTimeout = setTimeout(() => {
				fireAnalyticsEvent(onPersonCardOrgChartDwelled(commonAttributes));
			}, ORG_CHART_DWELL_THRESHOLD);
		}

		return () => {
			clearTimeout(dwellTimeout);
		};
	}, [isOrgChartModalOpen, fireAnalyticsEvent, commonAttributes]);

	const subText = [jobTitle, department].filter((text) => !!text).join(', ');
	const trimmedSubText = subText.length > 250 ? `${subText.slice(0, 250)}...` : subText;

	const orgText = <Text color="color.text.subtlest">{trimmedSubText}</Text>;

	const timeAndLocationComponent = (location || time) && (
		<Inline alignBlock="center">
			<Box xcss={IconPaddingStyles.default}>
				<LocationIcon label="" />
			</Box>
			<Text size="medium" color="color.text.subtlest">
				<Inline space="space.050">
					{location && <Text>{location}</Text>}
					{time && location && <Text>•</Text>}
					{time && <Text>{time}</Text>}
				</Inline>
			</Text>
		</Inline>
	);

	const additionalInfo = (
		<>
			{timeAndLocationComponent}
			{showAITopics && (
				<KnowledgeCardsTopics
					cloudId={cloudId}
					userId={accountId ?? ''}
					userName={name}
					sessionId={sessionId}
					v2styles={true}
				/>
			)}
		</>
	);

	const media = <KnowledgeCardsPicture src={avatarUrl || ''} alt="" />;

	const handleLinkClick = (e: React.MouseEvent) => {
		if (fg('platform-adopt-teams-nav-config')) {
			profileOnNavigate(e);
		}
	};

	const openOrgChartModal = () => {
		setIsOrgChartModalOpen(true);
		fireAnalyticsEvent(onPersonCardOrgChartShown(commonAttributes));
	};
	const closeOrgChartModal = () => setIsOrgChartModalOpen(false);

	const orgChartButton =
		(isHello || fg('kd_person_card_org_chart_button')) && accountId ? (
			<IconButton
				icon={DepartmentIcon}
				label={formatMessage(messages.knowledge_cards_person_org_chart_label)}
				isTooltipDisabled={false}
				onClick={openOrgChartModal}
				testId="person-card-org-chart-button"
			/>
		) : null;

	const ClickWrapper = ({ children, userId }: { children: ReactNode; userId: string }) => (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
		<div
			key={`${userId}-org-chart-card`}
			onClick={() => {
				fireAnalyticsEvent(onPersonCardOrgChartCardClicked(commonAttributes));
			}}
		>
			{children}
		</div>
	);

	return (
		<>
			<KnowledgeCardHeader
				name={name}
				email={email}
				profileUrl={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl || ''}
				profileLinkText={formatMessage(messages.ai_answer_who_questions_view_profile)}
				media={media}
				orgText={orgText}
				additionalInfo={additionalInfo}
				onLinkClick={handleLinkClick}
				headerActions={orgChartButton}
				testId="person-knowledge-card-header"
			/>

			<ModalTransition>
				{accountId && isOrgChartModalOpen && (
					<Modal onClose={closeOrgChartModal} testId="person-card-org-chart-modal">
						<ModalHeader hasCloseButton>
							<ModalTitle>
								{formatMessage(messages.knowledge_cards_person_org_chart_modal_header)}
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<ReportingLines
								userId={accountId}
								product="personKnowledgeCardOrgChart"
								WrappedWithProfilecard={ClickWrapper}
							/>
						</ModalBody>
						<ModalFooter />
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};
